import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, X } from 'lucide-react';

interface Review {
  id: string;
  content: string;
  rating: number;
  profiles: {
    prenume: string;
    nume: string;
  } | null;
}

interface ReviewCarouselProps {
  reviews: Review[];
  showArrows?: boolean;
  showDots?: boolean;
}

interface ReviewModalProps {
  review: Review | null;
  onClose: () => void;
}

function ReviewModal({ review, onClose }: ReviewModalProps) {
  if (!review) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex flex-col gap-2">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-5 h-5 ${
                    i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            {review.profiles && (
              <h3 className="text-lg font-semibold text-gray-800">
                {review.profiles.prenume} {review.profiles.nume}
              </h3>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <p className="text-gray-600 whitespace-pre-wrap">{review.content}</p>
      </div>
    </div>
  );
}

export default function ReviewCarousel({
  reviews,
  showArrows = true,
  showDots = true,
}: ReviewCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [exitX, setExitX] = useState<number>(0);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  // If there are no reviews, show a placeholder
  if (!reviews.length) {
    return null;
  }

  const handleDragEnd = (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: { offset: { x: number } }
  ) => {
    if (Math.abs(info.offset.x) > 100) {
      setExitX(info.offset.x);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % reviews.length);
        setExitX(0);
      }, 200);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-5 h-5 ${
              i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="h-72 w-full flex items-center justify-center">
      <div className="relative w-[600px] h-64"> {/* Increased width from 320px (w-80) to 600px */}
        {reviews.map((review, index) => {
          const isCurrentCard = index === currentIndex;
          const isPrevCard =
            index === (currentIndex + 1) % reviews.length;
          const isNextCard =
            index === (currentIndex + 2) % reviews.length;

          if (!isCurrentCard && !isPrevCard && !isNextCard) return null;

          return (
            <motion.div
              key={review.id}
              className="absolute w-full h-full rounded-2xl bg-white shadow-xl"
              style={{
                zIndex: isCurrentCard ? 3 : isPrevCard ? 2 : 1,
              }}
              drag={isCurrentCard ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.7}
              onDragEnd={isCurrentCard ? handleDragEnd : undefined}
              initial={{
                scale: 0.95,
                opacity: 0,
                y: isCurrentCard ? 0 : isPrevCard ? 8 : 16,
                rotate: isCurrentCard ? 0 : isPrevCard ? -2 : -4,
              }}
              animate={{
                scale: isCurrentCard ? 1 : 0.95,
                opacity: isCurrentCard ? 1 : isPrevCard ? 0.6 : 0.3,
                x: isCurrentCard ? exitX : 0,
                y: isCurrentCard ? 0 : isPrevCard ? 8 : 16,
                rotate: isCurrentCard ? exitX / 20 : isPrevCard ? -2 : -4,
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 20,
              }}
            >
              {showArrows && isCurrentCard && (
                <div className="absolute inset-x-0 top-2 flex justify-between px-4">
                  <span className="text-2xl select-none cursor-pointer text-gray-300 hover:text-gray-400">
                    &larr;
                  </span>
                  <span className="text-2xl select-none cursor-pointer text-gray-300 hover:text-gray-400">
                    &rarr;
                  </span>
                </div>
              )}

              <div className="p-6 flex flex-col items-center gap-4">
                <div className="flex flex-col items-center gap-2">
                  {renderStars(review.rating)}
                  {review.profiles && (
                    <h3 className="text-lg font-semibold text-gray-800">
                      {review.profiles.prenume} {review.profiles.nume}
                    </h3>
                  )}
                </div>
                <p className="text-center text-sm text-gray-600 line-clamp-4">
                  {review.content}
                </p>
                {isCurrentCard && (
                  <button
                    className="text-sm text-[#007FFF] hover:text-[#024994] transition mt-2"
                    onClick={() => setSelectedReview(review)}
                  >
                    Vezi mai mult
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
        {showDots && (
          <div className="absolute -bottom-8 left-0 right-0 flex justify-center gap-2">
            {reviews.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex
                    ? 'bg-blue-500'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <ReviewModal 
        review={selectedReview} 
        onClose={() => setSelectedReview(null)} 
      />
    </div>
  );
}